import { Brand, createBranded } from '@deltasierra/type-utilities';
import { HasId, FullAuditable } from '../common/model/traits';
import { ClientId } from '../client/ClientId';
import { ObjectOrVoid, PathBuilder, ApiRoute, HttpMethod } from '../common/api/routes';

export interface BuilderTemplateCategory extends HasId<BuilderTemplateCategoryId>, FullAuditable {
    clientId: ClientId;
    title: string;
    startDate: Date;
    endDate: Date | null;
    order: number;
}

export type BuilderTemplateCategorySimpleDto = Pick<
    BuilderTemplateCategory,
    'clientId' | 'endDate' | 'id' | 'order' | 'title'
>;

export type BuilderTemplateCategoryAdminDto = Pick<
    BuilderTemplateCategory,
    'clientId' | 'endDate' | 'id' | 'order' | 'startDate' | 'title'
>;

export type BuilderTemplateCategoryAdminCreateUpdateDto = Partial<Pick<BuilderTemplateCategory, 'endDate'>> &
    Pick<BuilderTemplateCategory, 'startDate' | 'title'>;

export function isBuilderTemplateCategoryEndDateValid(builderTemplateCategory: {
    startDate: Date;
    endDate?: Date | null;
}): boolean {
    return (
        !builderTemplateCategory.endDate ||
        builderTemplateCategory.endDate.valueOf() >= builderTemplateCategory.startDate.valueOf()
    );
}

type ClientBuilderTemplateCategoriesCollectionApiParams = { clientId: ClientId };
type ClientBuilderTemplateCategoryApiParams = {
    clientId: ClientId;
    builderTemplateCategoryId: BuilderTemplateCategoryId;
};

const BUILDER_TEMPLATE_CATEGORIES_COLLECTION_API_ROOT = <T extends ObjectOrVoid>(path: PathBuilder<T>) =>
    path.literal('templateCategories');
const CLIENT_BUILDER_TEMPLATE_CATEGORIES_COLLECTION_API_ROOT = <
    T extends ClientBuilderTemplateCategoriesCollectionApiParams,
>(
    path: PathBuilder<T>,
) => path.literal('clients').param('clientId').literal('templateCategories');
const CLIENT_BUILDER_TEMPLATE_CATEGORY_API_ROOT = <T extends ClientBuilderTemplateCategoryApiParams>(
    path: PathBuilder<T>,
) => CLIENT_BUILDER_TEMPLATE_CATEGORIES_COLLECTION_API_ROOT(path).param('builderTemplateCategoryId');

export const BuilderTemplateCategoryApi = {
    createBuilderTemplateCategory: new ApiRoute<
        BuilderTemplateCategoryAdminDto,
        ClientBuilderTemplateCategoriesCollectionApiParams,
        BuilderTemplateCategoryAdminCreateUpdateDto,
        void
    >(HttpMethod.Post, CLIENT_BUILDER_TEMPLATE_CATEGORIES_COLLECTION_API_ROOT),
    deleteBuilderTemplateCategory: new ApiRoute<void, ClientBuilderTemplateCategoryApiParams, void, void>(
        HttpMethod.Delete,
        CLIENT_BUILDER_TEMPLATE_CATEGORY_API_ROOT,
    ),
    getAllActiveBuilderTemplateCategories: new ApiRoute<
        BuilderTemplateCategorySimpleDto[],
        void,
        void,
        { clientIds: ClientId[] }
    >(HttpMethod.Get, path => BUILDER_TEMPLATE_CATEGORIES_COLLECTION_API_ROOT(path).literal('active')),
    getAllBuilderTemplateCategories: new ApiRoute<
        BuilderTemplateCategoryAdminDto[],
        ClientBuilderTemplateCategoriesCollectionApiParams,
        void,
        void
    >(HttpMethod.Get, CLIENT_BUILDER_TEMPLATE_CATEGORIES_COLLECTION_API_ROOT),
    reorderBuilderTemplateCategories: new ApiRoute<
        void,
        ClientBuilderTemplateCategoryApiParams,
        { categoriesOrder: BuilderTemplateCategoryId[] },
        void
    >(HttpMethod.Put, CLIENT_BUILDER_TEMPLATE_CATEGORIES_COLLECTION_API_ROOT),
    updateBuilderTemplateCategory: new ApiRoute<
        BuilderTemplateCategoryAdminDto,
        ClientBuilderTemplateCategoryApiParams,
        BuilderTemplateCategoryAdminCreateUpdateDto,
        void
    >(HttpMethod.Patch, CLIENT_BUILDER_TEMPLATE_CATEGORY_API_ROOT),
};

export type BuilderTemplateCategoryId = Brand<number, 'BuilderTemplateCategoryId'>;
export const BuilderTemplateCategoryId = createBranded<BuilderTemplateCategoryId>();
